import React, { Suspense, lazy, useEffect } from 'react'
import './App.css'
import { Navigate, Route, Routes } from 'react-router-dom'
import MainLayout from './layouts/MainLayout/MainLayout'
import { RootState } from './redux/store'
import { connect } from 'react-redux'
import { authUser } from './domains/Login/store/auth.slice'
import MyLoader from './components/MyLoader/MyLoader'
import { setScreenHeight, setScreenWidth } from './redux/main.slice'
import { AuthUser } from './domains/Login/types/users'
import ModuleAccess, {
  PurchaseModuleAccess,
} from './components/ModuleAccess/ModuleAccess'
import {
  getDefaultAuthorizedRoute,
  getDefaultNotesRoute,
} from './utils/moduleAccess'

const HomePage = lazy(() => import('./domains/Home/pages/HomePage'))
const LoginPage = lazy(() => import('./domains/Login/pages/LoginPage'))
const ObjectivesMain = lazy(
  () => import('./domains/Objectives/pages/ObjectivesMain/ObjectivesMain'),
)
const CreateNewObjective = lazy(
  () =>
    import('./domains/Objectives/pages/CreateNewObjective/CreateNewObjective'),
)
const Objective = lazy(() => import('./domains/Objectives/pages/Objective'))
const TranslationMain = lazy(
  () => import('./domains/Translations/pages/TranslationMain'),
)
const Settings = lazy(() => import('./domains/Settings/pages/Settings'))
const NotesPage = lazy(() => import('./domains/Notes/pages/NotesPage'))
const EventsPage = lazy(() => import('./domains/Events/pages/EventsPage'))
const ScenariosPage = lazy(
  () => import('./domains/Scenarios/pages/ScenariosPage'),
)
const ScenarioDetailPage = lazy(
  () => import('./domains/Scenarios/pages/ScenarioDetailPage'),
)
const MapConstructorPage = lazy(
  () => import('./domains/Scenarios/pages/MapConstructorPage'),
)
const WhiteboardPage = lazy(
  () => import('./domains/Whiteboard/pages/WhiteboardPage/WhiteboardPage'),
)
const WhiteboardListPage = lazy(
  () =>
    import('./domains/Whiteboard/pages/WhiteboardListPage/WhiteboardListPage'),
)
const PurchasesPage = lazy(
  () => import('./domains/Purchase/pages/PurchasesPage'),
)
const FilesPage = lazy(() => import('./domains/Files/pages/FilesPage'))
const WeddingInvitation = lazy(
  () => import('./domains/wedding/pages/WeddingInvitation/WeddingInvitation'),
)
const WeddingPublicParticipants = lazy(
  () =>
    import(
      './domains/wedding/pages/WeddingPublicParticipants/WeddingPublicParticipants'
    ),
)
const SearchPage = lazy(() => import('./domains/Search/SearchPage'))

interface AppProps {
  user?: AuthUser
  isAuthLoading: boolean
  authUser: () => void
  setScreenHeight(height: number): void
  setScreenWidth(width: number): void
}

function App({
  user,
  isAuthLoading,
  authUser,
  setScreenHeight,
  setScreenWidth,
}: AppProps) {
  useEffect(() => {
    authUser()
    setScreenHeight(window.innerHeight)
    setScreenWidth(window.innerWidth)
  }, [])

  const hashPath = window.location.hash.slice(1)
  const isPublicRoute = hashPath.startsWith('/public/')
  const defaultAuthorizedRoute = getDefaultAuthorizedRoute(user)
  const defaultNotesRoute = getDefaultNotesRoute(user) || defaultAuthorizedRoute
  const unauthorizedFallback = defaultAuthorizedRoute || '/'

  const renderModuleRoute = (
    code: string,
    element: React.ReactNode,
    requiredPermission: 'view' | 'edit' = 'view',
  ) => (
    <ModuleAccess
      user={user}
      code={code}
      requiredPermission={requiredPermission}
      fallbackPath={unauthorizedFallback}
    >
      {element}
    </ModuleAccess>
  )

  if (isPublicRoute) {
    return (
      <Suspense fallback={<MyLoader />}>
        <Routes>
          <Route
            path="/public/wedding/:eventSlug/invitation"
            element={<WeddingInvitation />}
          />
          <Route
            path="/public/wedding/:eventSlug/participants"
            element={<WeddingPublicParticipants />}
          />
          <Route
            path="/public/wedding/participants"
            element={<WeddingPublicParticipants />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    )
  }

  if (isAuthLoading) {
    return <MyLoader />
  }

  if (!user) {
    return (
      <Suspense fallback={<MyLoader />}>
        <LoginPage />
      </Suspense>
    )
  }

  return (
    <div className="App">
      <MainLayout>
        <Suspense fallback={<MyLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/notes"
              element={
                defaultNotesRoute ? (
                  <Navigate to={defaultNotesRoute} replace />
                ) : (
                  <Navigate to={unauthorizedFallback} replace />
                )
              }
            />
            <Route path="/notes/*" element={<NotesPage />} />
            <Route
              path="/objectives"
              element={renderModuleRoute('objectives', <ObjectivesMain />)}
            />
            <Route
              path="/objectives/create-new-objective"
              element={renderModuleRoute(
                'objectives',
                <CreateNewObjective />,
                'edit',
              )}
            />
            <Route
              path="/objective/:name/:id"
              element={renderModuleRoute('objectives', <Objective />)}
            />
            <Route
              path="/events"
              element={renderModuleRoute('events', <EventsPage />)}
            />
            <Route
              path="/search"
              element={
                user.role === 'admin' ? (
                  <SearchPage />
                ) : (
                  <Navigate to={unauthorizedFallback} replace />
                )
              }
            />
            <Route
              path="/translation"
              element={renderModuleRoute('translation', <TranslationMain />)}
            />
            <Route
              path="/whiteboards"
              element={renderModuleRoute('whiteboards', <WhiteboardListPage />)}
            />
            <Route
              path="/files"
              element={renderModuleRoute('files', <FilesPage />)}
            />
            <Route
              path="/whiteboards/:id"
              element={renderModuleRoute(
                'whiteboards',
                <WhiteboardPage />,
                'edit',
              )}
            />
            <Route
              path="/scenarios"
              element={renderModuleRoute('scenarios', <ScenariosPage />)}
            />
            <Route
              path="/scenarios/:id"
              element={renderModuleRoute('scenarios', <ScenarioDetailPage />)}
            />
            <Route
              path="/scenarios/:id/maps/:mapId"
              element={renderModuleRoute('scenarios', <MapConstructorPage />)}
            />
            <Route
              path="/purchases"
              element={
                <PurchaseModuleAccess
                  user={user}
                  fallbackPath={unauthorizedFallback}
                >
                  <PurchasesPage />
                </PurchaseModuleAccess>
              }
            />
            <Route
              path="/settings/*"
              element={renderModuleRoute('settings', <Settings />)}
            />
            <Route
              path="*"
              element={<Navigate to={unauthorizedFallback} replace />}
            />
          </Routes>
        </Suspense>
      </MainLayout>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  isAuthLoading: state.auth.isAuthLoading,
})

export default connect(mapStateToProps, {
  authUser,
  setScreenHeight,
  setScreenWidth,
})(App)
